/**
 * Юнит-экономика партии: единственное место, где живут формулы прибыли.
 *
 * Вынесено из `CalculatorSummary.tsx` не ради красоты, а ради проверяемости.
 * Формулы стояли внутри компонента, и защищены не были ничем: умножение ставки
 * купона на десять оставляло весь фронтовый набор тестов зелёным (проверено
 * мутацией при ревью 2026-08-24). По этим числам принимают решение о закупке,
 * и тихо смещённый результат хуже явного падения — поэтому расчёт отделён от
 * рендера и закрыт golden-тестом на полный снимок значений.
 *
 * Единицы: цены и комиссии — доллары, `referralFee`/`otherFees`/`returnRate`/
 * `resellableReturns`/`conversionRate`/`ppcSalesPercentage` — проценты.
 */

/** Вход расчёта — ровно те поля калькулятора, из которых считается экономика. */
export interface UnitEconomicsInput {
  purchasePrice: number;
  shippingPrice: number;
  storageFee: number;
  sellingPrice: number;
  referralFee: number;
  fbaFee: number;
  otherFees: number;
  ppcCost: number;
  conversionRate: number;
  ppcSalesPercentage: number;
  returnRate: number;
  resellableReturns: number;
  batchQuantity: number;
}

export interface UnitEconomics {
  cogs: number;
  referralFeeAmount: number;
  otherFeesAmount: number;
  returnLossPerUnit: number;
  grossProfitPerUnit: number;
  finalProfitPerUnit: number;
  marginPerUnit: number;
  grossRoi: number;
  batchQty: number;
  batchCogs: number;
  batchRevenue: number;
  batchPpcCost: number;
  batchStorageFee: number;
  couponPromoCost: number;
  batchFinalProfit: number;
  batchProfit: number;
  batchMargin: number;
  batchROI: number;
  batchProfitWithLaunch: number;
  batchMarginWithLaunch: number;
  batchROIWithLaunch: number;
  returnQty: number;
  batchReturnLoss: number;
  orderCost: number;
  ppcOrderProfit: number;
  advertisingCostPerUnit: number;
  couponPromoCostPerUnit: number;
  profitPerUnitWithCosts: number;
  marginPerUnitWithCosts: number;
  roiPerUnitWithCosts: number;
  /** МЗЦ — максимальная закупочная цена под целевую маржу. */
  maxPurchasePrice: number;
}

/** Доля покупателей, получающих купон, и фиксированная надбавка на выдачу. */
const COUPON_SHARE_OF_BATCH = 0.35;
const COUPON_DISCOUNT_RATE = 0.05;
const COUPON_HANDLING_PER_UNIT = 0.6;

/**
 * Маржа, на которую считается МЗЦ.
 * Документ «Пайплайн 3_08», приоритет 1: «маржа для расчёта берётся 30%».
 * Доля, а не проценты — чтобы формула читалась как в ТЗ (Price × 0,3).
 */
export const MAX_PURCHASE_PRICE_TARGET_MARGIN = 0.3;

/**
 * МЗЦ = Profit/unit (со всеми расходами, без launch budget)
 *     + Purchase $ (если есть) − Price × 0,3.
 *
 * Это потолок закупки, а не пол: выше неё маржа уходит ниже targetMargin,
 * ниже — поднимается выше.
 *
 * Зеркало backend/services/calculator.py::calculate_max_purchase_price. Прибыль
 * на вход подаётся уже посчитанная — второго расчёта прибыли формула не заводит.
 *
 * Прибыль на единицу линейна по закупочной цене с коэффициентом −1 (закупка
 * входит только в COGS), поэтому `profit + purchase` — это прибыль при нулевой
 * закупке, а результат не зависит от того, введена себестоимость или нет. Отсюда
 * оба правила ТЗ: при невведённой себестоимости МЗЦ и есть себестоимость под
 * маржу 30%, при введённой вручную — значение МЗЦ для 30% сохраняется, меняется
 * только фактическая маржа.
 */
export function calcMaxPurchasePrice(
  profitPerUnitWithCosts: number,
  purchasePrice: number,
  sellingPrice: number,
  targetMargin: number = MAX_PURCHASE_PRICE_TARGET_MARGIN,
): number {
  return profitPerUnitWithCosts + purchasePrice - sellingPrice * targetMargin;
}

export function computeUnitEconomics(
  d: UnitEconomicsInput,
  launchBudget: number,
  couponPromoEnabled: boolean,
): UnitEconomics {
  const cogs = d.purchasePrice + d.shippingPrice;
  const referralFeeAmount = (d.sellingPrice * d.referralFee) / 100;
  const otherFeesAmount = (d.sellingPrice * d.otherFees) / 100;

  // Потеря на возвратах: цена × доля возвратов × доля непригодных к перепродаже.
  const returnLossPerUnit =
    d.sellingPrice * (d.returnRate / 100) * (1 - d.resellableReturns / 100);

  const grossProfitPerUnit =
    d.sellingPrice - cogs - referralFeeAmount - d.fbaFee - otherFeesAmount;
  const finalProfitPerUnit = grossProfitPerUnit - returnLossPerUnit;
  const marginPerUnit = d.sellingPrice > 0 ? (grossProfitPerUnit / d.sellingPrice) * 100 : 0;
  const grossRoi = cogs > 0 ? (grossProfitPerUnit / cogs) * 100 : 0;

  // Партия берётся как есть, включая ноль: подстановка «типовых» 400 штук
  // рисовала бы прибыль и ROI партии, которую пользователь не заказывал.
  const batchQty = d.batchQuantity;
  const batchCogs = batchQty * cogs;
  const batchRevenue = batchQty * d.sellingPrice;

  // Конверсия — делитель, и ноль в ней означает «не задана», а не «нулевая»:
  // поле стартует с нуля, и Keepa его не заполняет. Пока здесь подставлялся 1 %,
  // реклама партии раздувалась в сто раз; в прогнозе на том же экране стояла
  // подстановка 10 %, а бэкенд писал в сохранённый расчёт ноль — три разных
  // числа за один расчёт. Не задана — рекламы в расчёте нет.
  const conversionRate = d.conversionRate > 0 ? d.conversionRate / 100 : 0;
  const ppcSalesRate = d.ppcSalesPercentage / 100;
  const batchPpcCost =
    conversionRate > 0 ? ((batchQty * ppcSalesRate) / conversionRate) * d.ppcCost : 0;

  const batchStorageFee = batchQty * d.storageFee;
  const couponPromoCost = couponPromoEnabled
    ? COUPON_SHARE_OF_BATCH * batchQty * d.sellingPrice * COUPON_DISCOUNT_RATE +
      COUPON_HANDLING_PER_UNIT * (COUPON_SHARE_OF_BATCH * batchQty)
    : 0;

  const batchFinalProfit = finalProfitPerUnit * batchQty;
  const batchProfit = batchFinalProfit - batchPpcCost - batchStorageFee - couponPromoCost;
  const batchMargin = batchRevenue > 0 ? (batchProfit / batchRevenue) * 100 : 0;
  const batchROI = batchCogs > 0 ? (batchProfit / batchCogs) * 100 : 0;

  const batchProfitWithLaunch = batchProfit - launchBudget;
  const batchMarginWithLaunch =
    batchRevenue > 0 ? (batchProfitWithLaunch / batchRevenue) * 100 : 0;
  const batchROIWithLaunch = batchCogs > 0 ? (batchProfitWithLaunch / batchCogs) * 100 : 0;

  const returnQty = Math.round(batchQty * (d.returnRate / 100));
  const batchReturnLoss = batchQty * returnLossPerUnit;

  const orderCost = conversionRate > 0 ? d.ppcCost / conversionRate : 0;
  const ppcOrderProfit = grossProfitPerUnit - orderCost;

  const advertisingCostPerUnit = batchQty > 0 ? batchPpcCost / batchQty : 0;
  const couponPromoCostPerUnit =
    couponPromoEnabled && batchQty > 0 ? couponPromoCost / batchQty : 0;
  const profitPerUnitWithCosts =
    finalProfitPerUnit - advertisingCostPerUnit - d.storageFee - couponPromoCostPerUnit;
  const marginPerUnitWithCosts =
    d.sellingPrice > 0 ? (profitPerUnitWithCosts / d.sellingPrice) * 100 : 0;
  const roiPerUnitWithCosts = cogs > 0 ? (profitPerUnitWithCosts / cogs) * 100 : 0;

  // МЗЦ берёт уже готовую прибыль единицы со всеми расходами и без launch budget —
  // ту же, что показана рядом с ней в сводке.
  const maxPurchasePrice = calcMaxPurchasePrice(
    profitPerUnitWithCosts,
    d.purchasePrice,
    d.sellingPrice,
  );

  return {
    cogs,
    referralFeeAmount,
    otherFeesAmount,
    returnLossPerUnit,
    grossProfitPerUnit,
    finalProfitPerUnit,
    marginPerUnit,
    grossRoi,
    batchQty,
    batchCogs,
    batchRevenue,
    batchPpcCost,
    batchStorageFee,
    couponPromoCost,
    batchFinalProfit,
    batchProfit,
    batchMargin,
    batchROI,
    batchProfitWithLaunch,
    batchMarginWithLaunch,
    batchROIWithLaunch,
    returnQty,
    batchReturnLoss,
    orderCost,
    ppcOrderProfit,
    advertisingCostPerUnit,
    couponPromoCostPerUnit,
    profitPerUnitWithCosts,
    marginPerUnitWithCosts,
    roiPerUnitWithCosts,
    maxPurchasePrice,
  };
}
